import Utils from '@/utils/QSUtils';
import FormsService from '@/services/3.6/FormsService';
import WebEnvConfigService from '@/services/TheWebEnvConfigService';
import IFieldData from '@/models/forms/IFieldData';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import { IAdditionalFields, IDataPointsAddFields, newAdditionalFields } from '@/models/AdditionalFields';
import IFormCodesFields from '@/models/forms/IFormCodesFields';
import FormsHelper from './helpers/FormsHelper';
import IFormCodeFields from '@/models/forms/IFormCodeFields';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import TheWebEnvConfigService from '@/services/TheWebEnvConfigService';
import SettingsService from '@/services/3.6/SettingsService';
import FormsManager from '@/manager/3.6/FormsManager';
import IFormFieldsAdditionalDetails from '@/models/forms/IFormFieldsAdditionalDetails';
import IFormCodeFieldsAdditionalDetails from '@/models/forms/IFormCodeFieldsAdditionalDetails';
import { IRowVisibility } from '@/models/comp/IFormFillerData';
import IFieldSubjectCompRelationship from '@/models/forms/IFieldSubjectCompRelationship';

// Internal only: The set of mutation method names as strings.
// Actions call mutations via this.context.commit() with the first argument as the mutation method name.
const Mutate = {
    updateFormFields: 'updateFormFields',
    updateAdditionalFields: 'updateAdditionalFields',
    updateSubjectCompRelationship: 'updateSubjectCompRelationship',
};

interface IAddFieldsSave {
    data: IAdditionalFields[];
    formId: string | null;
}

interface IFormFields extends Array<IFormCodeFields> {}

/**
 * This store file is implemented using vuex-module-decorators.
 * https://github.com/championswimmer/vuex-module-decorators
 * https://championswimmer.in/vuex-module-decorators/pages/overview.html
 *
 * Getters at the top, Mutators at the bottom.
 * All Actions have a summary indicating they're an action (intellisense purposes).
 * All Actions are async since a Promise is implemented internally.
 */
@Module({ namespaced: true, name: 'formFieldV36' })
export default class FormFieldStoreModuleV36 extends VuexModule {
    public formCode = '';

    // Private are the store properties
    private additionalFields: IDataPointsAddFields | null = null;
    private formFields: IFormFields = [];
    private formFieldsAdditionalDetails: IFormFieldsAdditionalDetails = [];
    private parentChildFields: Record<string, string[]> = {};
    private rowVisibilityGrid: IRowVisibility = {};
    private rowVisibilityReport: IRowVisibility = {};
    private subjectCompRelationship: IFieldSubjectCompRelationship | null = null;

    get AdditionalFields(): IDataPointsAddFields | null {
        return this.additionalFields;
    }

    get FormFields(): any {
        const formCode = theLocalStorageUtils.get(LocalStorageKeys.formCode) || '';
        return (this.formFields as any)[formCode];
    }

    get FormFieldsAdditionalDetails(): IFormFieldsAdditionalDetails | null {
        const formCode = theLocalStorageUtils.get(LocalStorageKeys.formCode) || '';
        return (this.formFieldsAdditionalDetails as any)[formCode];
    }

    get ParentChildFields(): any {
        return this.parentChildFields;
    }

    get RowVisibilityGrid(): IRowVisibility {
        return this.rowVisibilityGrid;
    }

    get RowVisibilityReport(): IRowVisibility {
        return this.rowVisibilityReport;
    }

    get SubjectCompRelationship(): IFieldSubjectCompRelationship | null {
        return this.subjectCompRelationship;
    }

    @Action
    async saveRowVisibilityGrid(payload: IRowVisibility) {
        this.context.commit('updateRowVisibilityGrid', payload);
    }

    @Action
    async saveRowVisibilityReport(payload: IRowVisibility) {
        this.context.commit('updateRowVisibilityReport', payload);
    }

    @Action
    async saveSubjectCompRelationship(payload: IFieldSubjectCompRelationship) {
        this.context.commit('updateSubjectCompRelationship', payload);
    }

    /**
     * Action:
     */
    @Action
    async saveDataPreferenceSourceOrder(payload: any) {
        const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();
        const res = await new FormsService(webEnvConfig).saveDataPreferenceSourceOrder(payload);
        if (res.status === 200) {
            await FormsManager.fetchAndStoreFormFields(theLocalStorageUtils.get(LocalStorageKeys.formCode) || '');
        } else {
            console.log('FormFieldStoreModule.saveDataPreferenceSourceOrder bad response');
        }
    }

    @Action
    async savePreferredCasing(payload: any) {
        const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();
        const res = await new FormsService(webEnvConfig).savePreferredCasing(payload);
        if (res.status === 200) {
            const formCode = theLocalStorageUtils.get(LocalStorageKeys.formCode) || '';
            await FormsManager.fetchAndStoreFormFields(formCode);
            this.context.commit('updatePreferredCasing', {
                formCode: formCode,
                fieldId: payload.fieldId,
                preferredCasing: payload.preferredCasing,
            });
        } else {
            console.log('FormFieldStoreModule.savePreferredCasing bad response');
        }
    }

    @Action
    async saveFieldRules(payload: any) {
        const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();
        const res = await new FormsService(webEnvConfig).saveFieldRules(payload);
        if (res.status === 200) {
            await FormsManager.fetchAndStoreFormFields(theLocalStorageUtils.get(LocalStorageKeys.formCode) || '');
        } else {
            console.log('FormFieldStoreModule.saveFieldRules bad response');
        }
    }

    @Action
    async saveSuggestFormattingRule(payload: any) {
        const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();
        const res = await new FormsService(webEnvConfig).saveSuggestFormattingRule(payload);
        if (res.status === 200) {
            await FormsManager.fetchAndStoreFormFields(theLocalStorageUtils.get(LocalStorageKeys.formCode) || '');
        } else {
            console.log('FormFieldStoreModule.saveSuggestFormattingRule bad response');
        }
    }


    // @Action
    // async saveAdditionalFields(payload: IAddFieldsSave) {
    //     const webEnvConfig = await WebEnvConfigService.getInstance().getConfigByHost();
    //     const res = await new FormsService(webEnvConfig).putAdditionalFields(payload);
    //     if (res.status === 200) {
    //         await FormsManager.fetchAndStoreAdditionalFields();
    //     } else {
    //         console.log('FormFieldStoreModule.saveAdditionalFields bad response');
    //     }
    // }

    /**
     * Mutation:
     */
    @Mutation
    updateAdditionalFields(payload: IDataPointsAddFields) {
        this.additionalFields = payload;
    }

    @Mutation
    updateFields(payload: IFormCodeFields) {
        if (payload.formCode) {
            (this.formFields as any)[payload.formCode] = payload.formFields;
        }
    }

    @Mutation
    updateRowVisibilityGrid(payload: IRowVisibility) {
        this.rowVisibilityGrid = { ...this.rowVisibilityGrid, ...payload };
    }

    @Mutation
    updateRowVisibilityReport(payload: IRowVisibility) {
        this.rowVisibilityReport = { ...this.rowVisibilityReport, ...payload };
    }

    @Mutation
    updateSubjectCompRelationship(payload: IFieldSubjectCompRelationship) {
        this.subjectCompRelationship = payload;
    }

    @Mutation
    updatePreferredCasing(payload: any) {
        const formFields = (this.formFields as any)[payload.formCode];
        if (!Array.isArray(formFields)) {
            return;
        }

        const parentFieldId = Math.floor(payload.fieldId);
        (this.formFields as any)[payload.formCode] = formFields.map((field: any) =>
            field.fieldId === parentFieldId
                ? { ...field, preferredCasing: payload.preferredCasing }
                : field
        );
    }

    @Mutation
        updateFieldsAdditionalDetails(payload: IFormCodeFieldsAdditionalDetails) {
            if (payload.formCode) {
                (this.formFieldsAdditionalDetails as any)[payload.formCode] = payload.formFields;

                const linked: Record<string, string[]> = {};
                for (const field of payload.formFields ?? []) {
                    const fieldName = field?.fieldName?.replace(/[\r\n]/g, '');
                    const parentFieldName = field?.parentFieldName?.replace(/[\r\n]/g, '').replace(/_FLATTENED$/, '');
                    if (!parentFieldName || !fieldName) {
                        continue;
                    }

                    if (!linked[parentFieldName]) {
                        linked[parentFieldName] = [];
                    }

                    if (!linked[parentFieldName].includes(fieldName)) {
                        linked[parentFieldName].push(fieldName);
                    }
                }

                this.parentChildFields = linked;

                //console.log("this.formFieldsLinked", this.parentChildFields);
            }
        }
    }
